'use client';

import Bugsnag from '@bugsnag/js';
import { createIntl } from '@formatjs/intl';
import { useSearchParams } from 'next/navigation';
import type { userAgent } from 'next/server';
// eslint-disable-next-line @alltrails/custom/react-imports
import React, { Fragment, PropsWithChildren, useRef } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { isValidUnitOverride } from '@alltrails/core/utils/isValidUnitOverride';
import setGlobalFetchHeaders from '@alltrails/core/utils/setGlobalFetchHeaders';
import type { ListsState } from '@alltrails/lists/slices/lists';
import type { MapState } from '@alltrails/maps/redux/reducer';
import type { Bootstrap } from '@alltrails/networking/types/Bootstrap';
import type Experiments from '@alltrails/types/Experiments';
import { setupListeners } from '@/utils/listenerMiddleware';
import { AppStore, buildStore } from '@/utils/redux';

setupListeners();

type Props = {
  bootstrap: Bootstrap;
  browser: ReturnType<typeof userAgent>['browser'];
  experiments: Experiments;
  map: MapState;
  messages: ReturnType<typeof createIntl>['messages'];
  lists: ListsState;
  locale: ReturnType<typeof createIntl>['locale'];
  irclickid?: string;
};

const plugin = Bugsnag.getPlugin('react');
const ErrorBoundary = plugin ? plugin.createErrorBoundary(React) : Fragment;

export default function CustomProvider({
  bootstrap,
  browser,
  experiments,
  map,
  messages,
  lists,
  locale,
  children,
  irclickid
}: PropsWithChildren<Props>) {
  const languageRegionCode = useLanguageRegionCode();
  setGlobalFetchHeaders({ languageRegionCode });
  const searchParams = useSearchParams();
  const unitOverride = searchParams.get('u');
  const context = { ...bootstrap.context };
  if (unitOverride && isValidUnitOverride(unitOverride)) {
    context.displayMetric = unitOverride === 'm';
  }

  const storeRef = useRef<AppStore | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = buildStore({
      algoliaConfigs: bootstrap.algoliaConfig,
      amplitudeAnalyticsSettings: bootstrap.amplitudeAnalyticsSettings,
      browser,
      context,
      currencyCode: bootstrap.currencyCode,
      experiments,
      features: bootstrap.features,
      irclickid,
      lists,
      map,
      plans: bootstrap.plans,
      requestCountryCode: bootstrap.requestCountryCode,
      waypoints: { loading: true, errors: [], waypoints: undefined }
    });
  }

  return (
    <ErrorBoundary>
      <IntlProvider messages={messages} locale={locale}>
        <Provider store={storeRef.current}>{children}</Provider>
      </IntlProvider>
    </ErrorBoundary>
  );
}
